import { getLangLabel } from '../utils/i18n';

/**
 * Renders one input (or textarea) per language in a responsive grid.
 *
 * Props:
 *   value       – i18n object, e.g. { pt: '...', en: '...', pt_BR: '...' }
 *   onChange     – (lang, value) => void
 *   languages    – string[] from supportedLanguages
 *   type         – 'input' | 'textarea' (default: 'input')
 *   required     – boolean — applies only to the first (default) language
 *   disabled     – boolean
 *   rows         – number (for textarea, default: 3)
 *   maxLength    – number
 *   placeholder  – string
 *   label        – string (optional field label rendered above the grid)
 */
export default function I18nField({
  value = {},
  onChange,
  languages = [],
  type = 'input',
  required = false,
  disabled = false,
  rows = 3,
  maxLength,
  placeholder,
  label,
}) {
  const inputCls =
    'w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100';

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {label} {required && '*'}
        </label>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {languages.map((lang, idx) => {
          const isDefault = idx === 0;
          const langLabel = getLangLabel(lang);

          return (
            <div key={lang}>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {langLabel}
                {isDefault && ' (Default)'}
                {isDefault && required && ' *'}
              </label>
              {type === 'textarea' ? (
                <textarea
                  value={value?.[lang] ?? ''}
                  onChange={(e) => onChange(lang, e.target.value)}
                  disabled={disabled}
                  required={isDefault && required}
                  rows={rows}
                  maxLength={maxLength}
                  placeholder={placeholder}
                  className={`${inputCls} resize-none`}
                />
              ) : (
                <input
                  type="text"
                  value={value?.[lang] ?? ''}
                  onChange={(e) => onChange(lang, e.target.value)}
                  disabled={disabled}
                  required={isDefault && required}
                  maxLength={maxLength}
                  placeholder={placeholder}
                  className={inputCls}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
